import React from "react";
import Modal from "react-responsive-modal";
import * as Icon from "react-feather";
import moment from "moment";
import clsx from "clsx";

import "./compilation-run-wizard.css";
import AFSService from "../../../../services/AFSService";
import UserService from "../../../../services/UserService";
import ClientService from "../../../../services/ClientService";
import PROVINCES from "../../../../constants/Provinces";
import WComponent from "../../../common/WComponent";

/**
 * CompilationRunWizard — unified compilation engagement wizard.
 *
 * Merges the old CompilationRunWizard (pipeline) and CompilationAgent (CSRS 4200 checklist)
 * into a single dominant flow. One path to AFS.
 *
 * Steps:
 *   1. CaseWare folder    — optional DBF upload for GIFI reverse-lookup
 *   2. Bank statements    — one or more CSV files
 *   3. Settings           — province, year-end, accountant name, opening balance
 *   4. CSRS 4200          — engagement acceptance, framework, notes, compilation notice;
 *                           footer "Compilation Run" button triggers the pipeline directly.
 *                           Run progress, errors, and any SUSPENSE items requiring an
 *                           accountant decision surface inline on this same step.
 *
 * Props:
 *   open: boolean
 *   onClose: function
 *   onComplete: function({ state, afs, noticeToReader, unresolvedItems, agentResult, compilationData })
 *   clientId: number
 *   clientName: string
 *   transactions: Array
 *   province: string
 *   yearEnd: string
 */

const STEPS = [
	{ id: "caseware",   title: "CaseWare Files",    subtitle: "Optional — import prior year DBF data" },
	{ id: "bank",       title: "Bank Statements",   subtitle: "CSV exports from bank portal" },
	{ id: "settings",   title: "Settings",          subtitle: "Engagement parameters" },
	{ id: "csrs",       title: "CSRS 4200",         subtitle: "Compilation engagement checklist" },
];

// ─── CSRS 4200 Constants ─────────────────────────────────────────────────────

const ACCEPTANCE_ITEMS = [
	{ id: "a1", text: "Management understands this is a compilation engagement \u2014 no audit or review is performed and no assurance is expressed.", ref: "4200.08" },
	{ id: "a2", text: "Management acknowledges responsibility for the preparation of the financial information, including the financial reporting framework.", ref: "4200.10(a)" },
	{ id: "a3", text: "The Compilation Notice will accompany the financial information whenever distributed to third parties.", ref: "4200.10(b)" },
	{ id: "a4", text: "We have sufficient knowledge of the applicable financial reporting framework and the industry.", ref: "4200.09" },
];

const BASIS_OPTIONS = [
	{ id: "aspe",  label: "Accounting Standards for Private Enterprises (ASPE)", detail: "Most commonly used for Canadian private companies. Full accrual basis per CPA Canada Handbook \u2014 Part II." },
	{ id: "tax",   label: "Income Tax Basis of Accounting", detail: "Prepared on the same basis as the entity\u2019s income tax return. An OCBOA." },
	{ id: "other", label: "Other Basis", detail: "Specify the basis used (e.g., cash basis, regulatory basis)." },
];

const NOTES_ALWAYS = [
	{ id: "n_basis",    text: "Basis of Accounting",            detail: "Always required \u2014 CSRS 4200.14(b)" },
	{ id: "n_policies", text: "Significant Accounting Policies", detail: "Always required \u2014 ASPE 1505" },
];

const NOTES_AS_APPLICABLE = [
	{ id: "n_business",    text: "Description of Business",          detail: "Nature of business, incorporation details" },
	{ id: "n_going",       text: "Going Concern",                   detail: "If there is doubt about ability to continue \u2014 ASPE 1400" },
	{ id: "n_related",     text: "Related Party Transactions",      detail: "If any related party transactions occurred \u2014 ASPE 3840" },
	{ id: "n_capasset",    text: "Capital Asset Continuity Schedule", detail: "If capital assets exist on BS \u2014 ASPE 3061" },
	{ id: "n_ltd",         text: "Long-term Debt",                  detail: "Notes payable, mortgages, debentures \u2014 ASPE 3210" },
	{ id: "n_shareholder", text: "Shareholder / Director Loans",    detail: "Loans to/from shareholders \u2014 ASPE 3840" },
	{ id: "n_lease",       text: "Operating Lease Commitments",     detail: "Future minimum lease payments \u2014 ASPE 3065" },
	{ id: "n_contingent",  text: "Contingent Liabilities",          detail: "Pending litigation, guarantees \u2014 ASPE 3290" },
	{ id: "n_subsequent",  text: "Subsequent Events",               detail: "Events after balance sheet date \u2014 ASPE 3820" },
	{ id: "n_tax",         text: "Income Taxes",                    detail: "If taxable corporation with deferred taxes \u2014 ASPE 3465" },
];

// ─── Helper ──────────────────────────────────────────────────────────────────

const CLIENT_TYPE_TO_ENTITY_TYPE = { corporate: "corporation", personal: "sole_prop", trust: "trust" };
const INVENTORY_INDUSTRIES = new Set(["Retail", "Manufacturing", "Food & Beverage"]);

function matchProvinceName(stateProv) {
	const input = String(stateProv || "").trim().toLowerCase();
	if (!input) return null;
	const province = PROVINCES.find(p => p.name.toLowerCase() === input || p.abbreviation.toLowerCase() === input);
	return province ? province.name : null;
}

function deriveMostRecentFiscalYearEnd(clientYearEnd) {
	const monthDay = moment(clientYearEnd).format("MM-DD");
	const candidate = moment(moment().year() + "-" + monthDay, "YYYY-MM-DD");
	if (candidate.isAfter(moment())) candidate.subtract(1, "year");
	return candidate.format("YYYY-MM-DD");
}

function CheckItem({ checked, onToggle, text, detail, disabled }) {
	return (
		<div className={clsx("crw__check-row", checked && "crw__check-row--checked")} onClick={disabled ? undefined : onToggle}>
			<div className={clsx("crw__checkbox", checked && "crw__checkbox--checked")}>
				{checked && <Icon.Check size={11} color="#fff" strokeWidth={3} />}
			</div>
			<div className="crw__check-text">
				<p className="crw__check-main">{text}</p>
				{detail && <p className="crw__check-detail">{detail}</p>}
			</div>
		</div>
	);
}

// ─── Main Component ──────────────────────────────────────────────────────────

class CompilationRunWizard extends WComponent {
	state = (() => {
		let { transactions: initialTransactions, cwFiles: initialCwFiles, province: initialProvince, yearEnd: initialYearEnd, csrsChecklist } = this.props;
		const userData = UserService.getUserData() || {};
		// Auto-skip to the first step that actually needs user input
		const hasCwFiles = initialCwFiles && initialCwFiles.length > 0;
		const hasTransactions = initialTransactions && initialTransactions.length > 0;
		const hasSettings = !!(initialProvince && initialYearEnd);
		const initialStep = hasTransactions && hasSettings ? 3 : hasTransactions ? 2 : hasCwFiles ? 1 : 0;
		const _ck = csrsChecklist || {};
		const ck = _ck;
		return {
			step: initialStep,

			// Step 1 — CaseWare
			dbfAccounts: hasCwFiles ? initialCwFiles : [],

			// Step 2 — Bank CSVs
			transactions: initialTransactions || [],
			signConventionConfirmed: false,
			signInverted: false,

			// Step 3 — Settings
			province: initialProvince || "Ontario",
			yearEnd: initialYearEnd || "",
			accountantName: [userData.firstName, userData.lastName].filter(Boolean).join(" "),
			firmName: userData.companyName || "",
			// Step 3b — Entity context
			entityType: _ck.entityType || "corporation",
			operatingProfile: _ck.operatingProfile || "service",

			// Step 4 — CSRS 4200 (restore from persisted checklist if available)
			accepted: ck.accepted || { a1: true, a2: true, a3: true, a4: true },
			basis: ck.basis || "aspe",
			otherBasis: ck.otherBasis || "",
			comparatives: ck.comparatives !== undefined ? ck.comparatives : true,
			notes: ck.notes || { n_basis: true, n_policies: true },
			noticeConfirmed: !!ck.noticeConfirmed,
			csrsSection: "notice", // acceptance | framework | notes | notice

			// Step 5 — SUSPENSE resolution
			unresolvedItems: [],
			suspenseResolutions: {},

			// Step 6 — Run state
			runState: "idle",
			engagementState: null,
			runError: null,
			crossCheckResults: null,
		};
	})();

	componentDidMount = async () => {
		await this.prefillSettingsFromClient();
	};

	prefillSettingsFromClient = async () => {
		const { clientId, province: initialProvince, yearEnd: initialYearEnd, csrsChecklist } = this.props;
		if (!clientId) return;

		const response = await ClientService.getClient({ clientId });
		const client = response && response.client;
		if (!client) return;

		const checklist = csrsChecklist || {};
		const updates = {};

		if (!initialProvince && client.state_prov) {
			const provinceName = matchProvinceName(client.state_prov);
			if (provinceName) updates.province = provinceName;
		}
		if (!initialYearEnd && client.year_end) {
			updates.yearEnd = deriveMostRecentFiscalYearEnd(client.year_end);
		}
		if (!checklist.entityType && client.type && CLIENT_TYPE_TO_ENTITY_TYPE[client.type]) {
			updates.entityType = CLIENT_TYPE_TO_ENTITY_TYPE[client.type];
		}
		if (!checklist.operatingProfile && client.industry) {
			updates.operatingProfile = INVENTORY_INDUSTRIES.has(client.industry) ? "inventory" : "service";
		}

		if (Object.keys(updates).length > 0) await this.update(updates);
	};

	handleCsvFileUpload = (e) => {
		let files = Array.from(e.target.files || []);
		let allRows = [];
		let pending = files.length;
		if (pending === 0) return;

		for (let file of files) {
			let reader = new FileReader();
			reader.onload = (ev) => {
				let text = ev.target.result;
				let rows = this.parseSimpleCsv(text);
				allRows = allRows.concat(rows);
				pending--;
				if (pending === 0) {
					this.setState(prev => ({ transactions: [...prev.transactions, ...allRows] }));
					this.update({ signConventionConfirmed: false });
					this.update({ signInverted: false });
				}
			};
			reader.readAsText(file);
		}
	}

	parseSimpleCsv = (text) => {
		let lines = text.split("\n").filter(Boolean);
		if (lines.length < 2) return [];
		let headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());
		let rows = [];
		for (let i = 1; i < lines.length; i++) {
			let cols = lines[i].split(",").map(c => c.trim().replace(/^"|"$/g, ""));
			let row = {};
			headers.forEach((h, idx) => { row[h] = cols[idx] || ""; });
			if (row.description || row.amount) {
				rows.push({
					raw_description: row.description || row.desc || row.narrative || "",
					amount: parseFloat(row.amount || row.debit || row.credit || 0),
					debit_credit: parseFloat(row.amount || 0) < 0 ? "CREDIT" : "DEBIT",
					date: row.date || "",
					account_number: row.account || row.account_number || "",
				});
			}
		}
		return rows;
	}

	buildCompilationData = () => {
		let { clientName } = this.props;
		let { basis, otherBasis, province, comparatives, notes, accountantName, firmName, yearEnd } = this.state;
		const basisLabel = basis === "aspe" ? "Accounting Standards for Private Enterprises (ASPE)"
			: basis === "tax" ? "Income Tax Basis of Accounting"
			: otherBasis || "Other Basis";
		const yearEndLabel = yearEnd ? moment(yearEnd).format("MMMM D, YYYY") : "[Year End]";
		const priorYearEndLabel = yearEnd ? moment(yearEnd).subtract(1, "year").format("MMMM D, YYYY") : "[Prior Year End]";
		const entityName = clientName || "[Entity Name]";
		const compilationNoticeText =
`On the basis of information provided by management, we have compiled the balance sheet of ${entityName} as at ${yearEndLabel}${comparatives ? ` and ${priorYearEndLabel}` : ""} and the statement${comparatives ? "s" : ""} of income and retained earnings for the year${comparatives ? "s" : ""} then ended, in accordance with ${basisLabel}.

We have not performed an audit or a review engagement in respect of this financial information and, accordingly, we express no assurance thereon.

Readers are cautioned that the financial information may not be appropriate for their purposes.`;
		return {
			basisOfAccounting: basisLabel,
			province: province,
			comparatives,
			notes: Object.keys(notes).filter(k => notes[k]),
			practitioner: accountantName,
			firm: firmName,
			noticeDate: moment().format("YYYY-MM-DD"),
			compilationNotice: compilationNoticeText,
		};
	}

	buildCsrsChecklist = () => {
		let { accepted, basis, otherBasis, comparatives, notes, noticeConfirmed, entityType, operatingProfile } = this.state;
		return {
			accepted,
			basis,
			otherBasis,
			comparatives,
			notes,
			noticeConfirmed,
			entityType,
			operatingProfile,
		};
	}

	handleCancel = () => {
		if (this.runHandle) {
			this.runHandle.abort();
			this.runHandle = null;
			this.update({ runState: "idle", runError: null });
		}
		this.props.onClose();
	};

	handleRunEngagement = () => {
		let { clientId, clientName, onComplete, onClose } = this.props;
		let { province, yearEnd, accountantName, firmName } = this.state;
		this.update({ runState: "running" });
		this.update({ runError: null });

		this.runHandle = AFSService.runCompilationEngagement({
			clientId,
			province,
			yearEnd,
			accountantName,
			firmName,
			onState: (stateEvent) => {
				this.update({ engagementState: stateEvent.state });
				if (stateEvent.crossCheckResults) this.update({ crossCheckResults: stateEvent.crossCheckResults });

				// If SUSPENSE items exist, surface them inline on the CSRS step
				if (stateEvent.unresolvedItems && stateEvent.unresolvedItems.length > 0) {
					this.update({ unresolvedItems: stateEvent.unresolvedItems });
					this.update({ runState: "idle" });
					return;
				}
			},
			onEvent: function () {},
			onComplete: (data) => {
				// Go straight to AFS view — agent runs separately from Agent tab
				if (onComplete) {
					onComplete({
						state: data.state || this.state.engagementState,
						afs: data.afs,
						yearEnd: data.yearEnd || yearEnd,
						entityName: data.entityName || clientName,
						noticeToReader: data.noticeToReader,
						unresolvedItems: this.state.unresolvedItems,
						suspenseResolutions: this.state.suspenseResolutions,
						compilationData: this.buildCompilationData(),
						csrsChecklist: this.buildCsrsChecklist(),
						crossCheckResults: data.crossCheckResults || this.state.crossCheckResults,
						crossCheckFacts: data.crossCheckFacts || null,
					});
				}
				onClose();
			},
			onError: (err) => {
				this.update({ runState: "error" });
				this.update({ runError: err });
			},
		});
	}

	renderStepContent() {
		let { clientName } = this.props;
		let { step, dbfAccounts, transactions, signConventionConfirmed, signInverted, province, yearEnd, accountantName, firmName, entityType, operatingProfile, accepted, basis, otherBasis, comparatives, notes, noticeConfirmed, csrsSection, unresolvedItems, suspenseResolutions, runState, runError } = this.state;

		// Derived
		const basisLabel = basis === "aspe" ? "Accounting Standards for Private Enterprises (ASPE)"
			: basis === "tax" ? "Income Tax Basis of Accounting"
			: otherBasis || "Other Basis";
		const yearEndLabel = yearEnd ? moment(yearEnd).format("MMMM D, YYYY") : "[Year End]";
		const priorYearEndLabel = yearEnd ? moment(yearEnd).subtract(1, "year").format("MMMM D, YYYY") : "[Prior Year End]";
		const entityName = clientName || "[Entity Name]";

		const compilationNoticeText =
`On the basis of information provided by management, we have compiled the balance sheet of ${entityName} as at ${yearEndLabel}${comparatives ? ` and ${priorYearEndLabel}` : ""} and the statement${comparatives ? "s" : ""} of income and retained earnings for the year${comparatives ? "s" : ""} then ended, in accordance with ${basisLabel}.

We have not performed an audit or a review engagement in respect of this financial information and, accordingly, we express no assurance thereon.

Readers are cautioned that the financial information may not be appropriate for their purposes.`;

		switch (step) {
			case 0:
				return (
					<div className="crw__step">
						<p className="crw__step-desc">
							Upload the CaseWare AM.dbf file (Account Master) for GIFI reverse-lookup. This allows prior-year balances to map directly to FS accounts without relying on keyword matching.
						</p>
						<p className="crw__step-optional">This step is optional. Skip if you don't have CaseWare data.</p>
						<label className="crw__upload-btn">
							<Icon.Upload size={14} /> Choose AM.dbf
							<input type="file" accept=".dbf" className="crw__file-input-hidden" onChange={e => {
								let file = e.target.files[0];
								if (file) this.update({ dbfAccounts: [{ fileName: file.name }] });
							}} />
						</label>
						{dbfAccounts.length > 0 && (
							<div className="crw__file-loaded">
								<Icon.CheckCircle size={14} /> {dbfAccounts[0].fileName || "CaseWare files"} loaded
							</div>
						)}
					</div>
				);

			case 1:
				return (
					<div className="crw__step">
						<p className="crw__step-desc">
							Upload one or more bank statement CSV files. Required columns: <strong>date</strong>, <strong>description</strong>, <strong>amount</strong>.
						</p>
						<label className="crw__upload-btn">
							<Icon.Upload size={14} /> Choose CSV Files
							<input type="file" accept=".csv" multiple className="crw__file-input-hidden" onChange={this.handleCsvFileUpload} />
						</label>
						{transactions.length > 0 && (
							<>
								<div className="crw__file-loaded">
									<Icon.CheckCircle size={14} /> {transactions.length} transactions loaded
									<button className="crw__clear-btn" onClick={() => { this.update({ transactions: [] }); this.update({ signConventionConfirmed: false }); }}>
										<Icon.X size={12} /> Clear
									</button>
								</div>
								{!signConventionConfirmed && (function () {
									// Find sample deposit and payment for preview
									let sampleDeposit = transactions.find(function (t) { return t.amount > 0; });
									let samplePayment = transactions.find(function (t) { return t.amount < 0; });

									return (
										<div className="crw__sign-confirm">
											<p className="crw__sign-confirm-title">Confirm the sign convention for this bank statement:</p>
											<div className="crw__sign-confirm-preview">
												{sampleDeposit && (
													<div className="crw__sign-confirm-row">
														<span className="crw__sign-confirm-desc">{sampleDeposit.raw_description}</span>
														<span className="crw__sign-confirm-amount crw__sign-confirm-amount--positive">${Math.abs(sampleDeposit.amount).toLocaleString("en-CA", { minimumFractionDigits: 2 })}</span>
													</div>
												)}
												{samplePayment && (
													<div className="crw__sign-confirm-row">
														<span className="crw__sign-confirm-desc">{samplePayment.raw_description}</span>
														<span className="crw__sign-confirm-amount crw__sign-confirm-amount--negative">-${Math.abs(samplePayment.amount).toLocaleString("en-CA", { minimumFractionDigits: 2 })}</span>
													</div>
												)}
											</div>
											<p className="crw__sign-confirm-question">
												{sampleDeposit ? "Is \"" + sampleDeposit.raw_description + "\" a deposit (money in)?" : "Are positive amounts deposits?"}
											</p>
											<div className="crw__sign-confirm-buttons">
												<button className="crw__sign-confirm-btn crw__sign-confirm-btn--yes" onClick={() => {
													this.update({ signConventionConfirmed: true });
													this.update({ signInverted: false });
												}}>
													Yes — positive = deposits
												</button>
												<button className="crw__sign-confirm-btn crw__sign-confirm-btn--no" onClick={() => {
													// Invert all amounts
													this.setState(function (prev) {
														return { transactions: prev.transactions.map(function (t) {
															return { ...t, amount: -t.amount, debit_credit: t.debit_credit === "DEBIT" ? "CREDIT" : "DEBIT" };
														}) };
													});
													this.update({ signConventionConfirmed: true });
													this.update({ signInverted: true });
												}}>
													No — positive = payments (invert signs)
												</button>
											</div>
										</div>
									);
								})()}
								{signConventionConfirmed && signInverted && (
									<div className="crw__file-loaded">
										<Icon.AlertTriangle size={14} color="#e6a117" /> Signs inverted — positive amounts are now deposits
									</div>
								)}
							</>
						)}
					</div>
				);

			case 2:
				return (
					<div className="crw__step">
						<div className="crw__settings-grid">
							<div className="crw__field">
								<label className="crw__label">Province *</label>
								<select className="crw__select" value={province} onChange={e => this.update({ province: e.target.value })}>
									{["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia", "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Yukon"].map(p => (
										<option key={p} value={p}>{p}</option>
									))}
								</select>
							</div>
							<div className="crw__field">
								<label className="crw__label">Year End *</label>
								<input
									type="date"
									className="crw__input"
									value={yearEnd}
									min="2000-01-01"
									max={moment().add(1, "year").format("YYYY-MM-DD")}
									onChange={e => {
										const v = e.target.value;
										// type=date allows 6-digit years if typed directly; only accept canonical YYYY-MM-DD
										if (!v || /^\d{4}-\d{2}-\d{2}$/.test(v)) this.update({ yearEnd: v });
									}}
								/>
							</div>
							<div className="crw__field">
								<label className="crw__label">Accountant Name *</label>
								<input className="crw__input" value={accountantName} onChange={e => this.update({ accountantName: e.target.value })} placeholder="Jane Smith, CPA" />
							</div>
							<div className="crw__field">
								<label className="crw__label">Firm Name</label>
								<input className="crw__input" value={firmName} onChange={e => this.update({ firmName: e.target.value })} placeholder="Smith & Associates LLP" />
							</div>
							<div className="crw__field">
								<label className="crw__label">Entity Type *</label>
								<select className="crw__select" value={entityType} onChange={e => this.update({ entityType: e.target.value })}>
									<option value="corporation">Corporation</option>
									<option value="sole_prop">Sole Proprietorship</option>
									<option value="partnership">Partnership</option>
									<option value="trust">Trust</option>
									<option value="nonprofit">Non-profit</option>
								</select>
							</div>
							<div className="crw__field">
								<label className="crw__label">Operating Profile</label>
								<select className="crw__select" value={operatingProfile} onChange={e => this.update({ operatingProfile: e.target.value })}>
									<option value="service">Service Business</option>
									<option value="inventory">Inventory / Retail</option>
									<option value="holdco">Holding Company</option>
									<option value="mixed">Mixed</option>
								</select>
							</div>
						</div>
					</div>
				);

			case 3:
				if (runState === "running") {
					return (
						<div className="crw__step">
							<div className="crw__run-stream">
								<div className="crw__run-stream__header">
									<span className="crw__run-stream__spinner" /> Building financial statements...
								</div>
								<p className="crw__step-desc crw__step-desc--muted">
									Classifying {transactions.length} transactions, building trial balance, running cross-checks, and generating AFS. This may take 15-30 seconds.
								</p>
							</div>
						</div>
					);
				}
				if (runState === "error") {
					return (
						<div className="crw__step">
							<div className="crw__run-error">
								<Icon.AlertCircle size={20} /> Error: {runError}
							</div>
						</div>
					);
				}
				if (unresolvedItems.length > 0) {
					return (
						<div className="crw__step">
							<p className="crw__step-desc">
								{unresolvedItems.length} transaction{unresolvedItems.length !== 1 ? "s" : ""} could not be classified. Each requires a decision before generating the AFS.
							</p>
							<div className="crw__suspense-table">
								{unresolvedItems.map((item, idx) => (
									<div key={idx} className="crw__suspense-row">
										<div className="crw__suspense-row__desc">{item.raw_description}</div>
										<div className="crw__suspense-row__amount">{item.amount}</div>
										<div className="crw__suspense-row__status">
											{suspenseResolutions[idx] ? (
												<span className="crw__suspense-resolved"><Icon.Check size={12} /> {suspenseResolutions[idx]}</span>
											) : (
												<span className="crw__suspense-unresolved">Unresolved</span>
											)}
										</div>
										<button className="crw__suspense-accept-btn" onClick={() => this.setState(prev => ({ suspenseResolutions: { ...prev.suspenseResolutions, [idx]: "Accepted as exception" } }))}>
											Accept exception
										</button>
									</div>
								))}
							</div>
						</div>
					);
				}
				return (
					<div className="crw__step">
						<div className="crw__csrs-nav">
							{[
								{ key: "acceptance", label: "Acceptance", icon: <Icon.Shield size={13} /> },
								{ key: "framework",  label: "Framework",  icon: <Icon.Layers size={13} /> },
								{ key: "notes",      label: "Notes",      icon: <Icon.FileText size={13} /> },
								{ key: "notice",     label: "Notice",     icon: <Icon.Edit3 size={13} /> },
							].map(s => (
								<div key={s.key} className={clsx("crw__csrs-nav-item", csrsSection === s.key && "crw__csrs-nav-item--active")} onClick={() => this.update({ csrsSection: s.key })}>
									{s.icon} {s.label}
								</div>
							))}
						</div>

						{csrsSection === "acceptance" && (
							<>
								<p className="crw__step-desc">Confirm each item with management before accepting this compilation engagement.</p>
								{ACCEPTANCE_ITEMS.map(item => (
									<CheckItem
										key={item.id}
										checked={!!accepted[item.id]}
										onToggle={() => this.setState(prev => ({ accepted: { ...prev.accepted, [item.id]: !prev.accepted[item.id] } }))}
										text={item.text}
										detail={"CSRS " + item.ref}
									/>
								))}
							</>
						)}

						{csrsSection === "framework" && (
							<>
								<p className="crw__step-desc">Select the financial reporting framework. This determines the Compilation Notice wording and note disclosures.</p>
								{BASIS_OPTIONS.map(opt => (
									<div key={opt.id} className={clsx("crw__radio", basis === opt.id && "crw__radio--selected")} onClick={() => this.update({ basis: opt.id })}>
										<div className="crw__radio-top">
											<div className={clsx("crw__radio-dot", basis === opt.id && "crw__radio-dot--selected")}>
												{basis === opt.id && <div className="crw__radio-dot-inner" />}
											</div>
											<span className="crw__radio-label">{opt.label}</span>
										</div>
										<p className="crw__radio-detail">{opt.detail}</p>
										{opt.id === "other" && basis === "other" && (
											<input
												className="crw__input crw__input--spaced"
												placeholder="Describe the basis of accounting used\u2026"
												value={otherBasis}
												onChange={e => { e.stopPropagation(); this.update({ otherBasis: e.target.value }); }}
												onClick={e => e.stopPropagation()}
											/>
										)}
									</div>
								))}
								<div className="crw__comparatives-row">
									<label className="crw__label">Include Comparative Figures</label>
									<div className="crw__toggle-row">
										<span className={clsx("crw__toggle-option", comparatives && "crw__toggle-option--active")} onClick={() => this.update({ comparatives: true })}>Yes</span>
										<span className={clsx("crw__toggle-option", !comparatives && "crw__toggle-option--active")} onClick={() => this.update({ comparatives: false })}>No</span>
									</div>
								</div>
							</>
						)}

						{csrsSection === "notes" && (
							<>
								<p className="crw__step-desc">Select the notes that should accompany the financial information.</p>
								<span className="crw__section-header">Always Required</span>
								{NOTES_ALWAYS.map(n => (
									<CheckItem key={n.id} checked disabled text={n.text} detail={n.detail} />
								))}
								<span className="crw__section-header">As Applicable</span>
								{NOTES_AS_APPLICABLE.map(n => (
									<CheckItem key={n.id} checked={!!notes[n.id]} onToggle={() => this.setState(prev => ({ notes: { ...prev.notes, [n.id]: !prev.notes[n.id] } }))} text={n.text} detail={n.detail} />
								))}
							</>
						)}

						{csrsSection === "notice" && (
							<>
								<p className="crw__step-desc">Review the Compilation Notice that will accompany the financial information.</p>
								<div className="crw__notice-box">{compilationNoticeText}</div>
								<CheckItem
									checked={noticeConfirmed}
									onToggle={() => this.setState(prev => ({ noticeConfirmed: !prev.noticeConfirmed }))}
									text="I confirm that this Compilation Notice is accurate and will accompany the financial information whenever distributed."
									detail="CSRS 4200.23"
								/>
							</>
						)}
					</div>
				);

			default:
				return null;
		}
	}

	render() {
		let { open } = this.props;
		let { step, transactions, signConventionConfirmed, province, yearEnd, accountantName, accepted, noticeConfirmed, runState } = this.state;

		// Derived
		const allAccepted = ACCEPTANCE_ITEMS.every(i => accepted[i.id]);

		const canNext = [
			true,                                     // CaseWare: optional
			transactions.length > 0 && signConventionConfirmed,  // Bank: transactions loaded + sign confirmed
			province.trim() && yearEnd.trim() && accountantName.trim(),  // Settings: required fields
			allAccepted && noticeConfirmed && accountantName.trim(),      // CSRS 4200: all accepted + notice confirmed (also the Compilation Run trigger)
		][step];

		return (
			<Modal
				styles={{ modal: { width: 620, padding: 0, borderRadius: 10, overflow: "hidden" } }}
				open={open}
				onClose={this.handleCancel}
				center
				showCloseButton={false}
			>
			<div className="crw__header">
				<div className="crw__template-bar">
					{[
						{ type: "compilation", label: "Compilation", icon: <Icon.FileText size={12} />, active: true },
						{ type: "review", label: "Review", icon: <Icon.Eye size={12} />, active: false },
						{ type: "audit", label: "Audit", icon: <Icon.Shield size={12} />, active: false },
					].map(t => (
						<div key={t.type} className={clsx("crw__template-option", t.active && "crw__template-option--active", !t.active && "crw__template-option--disabled")} title={t.active ? "" : "Coming soon"}>
							{t.icon} {t.label}
						</div>
					))}
				</div>
				<div className="crw__header-row">
					<div>
						<h2 className="crw__title">{STEPS[step].title}</h2>
					</div>
					<button className="crw__close-btn" onClick={this.handleCancel}><Icon.X size={18} /></button>
				</div>
				<div className="crw__stepper">
					{STEPS.map((s, i) => (
						<React.Fragment key={s.id}>
							<div className="crw__step-column">
								<div
									className={clsx("crw__step-dot", i < step && "crw__step-dot--done", i === step && "crw__step-dot--active")}
									onClick={() => i < step && this.update({ step: i })}
									style={i < step ? { cursor: "pointer" } : {}}
								>
									{i < step ? <Icon.Check size={13} strokeWidth={3} /> : i + 1}
								</div>
								<span className={clsx("crw__step-label-text", i === step && "crw__step-label-text--active")}>{s.title}</span>
							</div>
							{i < STEPS.length - 1 && (
								<div className={clsx("crw__step-line", i < step && "crw__step-line--done")} />
							)}
						</React.Fragment>
					))}
				</div>
			</div>

			<div className="crw__body">
				{this.renderStepContent()}
			</div>

			<div className="crw__footer">
				<div className="crw__footer-group">
					<button className="crw__btn-secondary" onClick={this.handleCancel}>Cancel</button>
					{step > 0 && runState === "idle" && (
						<button className="crw__btn-secondary" onClick={() => this.setState(prev => ({ step: prev.step - 1 }))}>
							<Icon.ChevronLeft size={14} /> Back
						</button>
					)}
				</div>
				<div className="crw__footer-group">
					<span className="crw__step-counter">{step + 1} of {STEPS.length}</span>
					{step < STEPS.length - 1 && (
						<button
							className={clsx("crw__btn-primary", !canNext && "crw__btn-primary--disabled")}
							disabled={!canNext}
							onClick={() => canNext && this.setState(prev => ({ step: prev.step + 1 }))}
						>
							Next <Icon.ChevronRight size={14} />
						</button>
					)}
					{step === STEPS.length - 1 && runState !== "running" && (
						<button
							className={clsx("crw__btn-primary", !canNext && "crw__btn-primary--disabled")}
							disabled={!canNext}
							onClick={() => canNext && this.handleRunEngagement()}
						>
							<Icon.Play size={14} /> Compilation Run
						</button>
					)}
				</div>
			</div>
		</Modal>
	);
	}
}

export default CompilationRunWizard;
